/**
 * Eine Messzeile je Zug für das Vorlesen. (15.10.2026)
 *
 * Martin, 15.10. 21:22: „Wie können wir das abfangen, dass nicht immer wieder solche Sachen passieren?“ Bis heute stand
 * ein Vorlesen nur als viele Einzelzeilen im Journal (`[tts-t]`, `[tts-neu]`, `[tts-gap]` …) — ob eine Antwort ruhig lief
 * oder fünfmal neu anlief, musste man von Hand zusammenzählen. Jetzt schreibt jede Schlange am Ende ihres Zuges EINE
 * Zeile (nur Zahlen, kein Text), aus der sich ein Tagesbericht ziehen lässt:
 *
 *   [tts-zug] stuecke=5 datei=5 pcm=0 neu=4 weiter=0 fehler=1 wachhund=0 luecke_max=14.17s wait_max=1ms synth_max=8099ms abbruch=pille
 *
 *  - `neu`: frische Anläufe eines stehenden Stücks (`[tts-neu]`), `weiter`: an der Stelle fortgesetzt (`[tts-weiter]`).
 *  - `fehler`: Stücke, die mit einem Fehler endeten (`via=error:…`), `wachhund`: vom Wachhund beendet.
 *  - `luecke_max`: die längste Stille in einem Lauf eines Stücks (Zeit minus Fortgang; beim PCM-Weg die gemessene Lücke)
 *    oder vor einem Stück (`wait`) — in Sekunden.
 *  - `wait_max`: wie lange die Wiedergabe höchstens auf eine Vertonung warten musste (ms) — das ist die Lücke durch
 *    langsame Synthese. `synth_max`: Einreihen bis Ton (ms); ab dem zweiten Stück enthält das das Abspielen der Stücke
 *    davor und ist darum KEINE Lücke (siehe die Sonde in tts-queue.ts).
 *  - `abbruch`: der Grund eines Abbruchs (pille, neue-antwort …) oder `-`.
 */
export class ZugMessung {
  stuecke = 0;
  datei = 0;
  pcm = 0;
  neu = 0;
  weiter = 0;
  fehler = 0;
  wachhund = 0;
  private lueckeMaxMs = 0;
  private waitMaxMs = 0;
  private synthMaxMs = 0;

  /** Ein Stück klang zum ersten Mal (die Sonde `[tts-t]`). */
  stueck(synthMs: number, waitMs: number, weg: "datei" | "pcm"): void {
    this.stuecke += 1;
    if (weg === "pcm") this.pcm += 1;
    else this.datei += 1;
    if (Number.isFinite(synthMs)) this.synthMaxMs = Math.max(this.synthMaxMs, synthMs);
    if (Number.isFinite(waitMs)) {
      this.waitMaxMs = Math.max(this.waitMaxMs, waitMs);
      this.luecke(waitMs);
    }
  }

  /** Eine Stille in Millisekunden (vor einem Stück oder in einem Lauf). */
  luecke(ms: number): void {
    if (Number.isFinite(ms) && ms > this.lueckeMaxMs) this.lueckeMaxMs = ms;
  }

  /** Ein Lauf des Datei-Wegs ist zu Ende: wie er endete, wie lange er dauerte und wie weit die Zeit vorrückte. */
  lauf(via: string, echtMs: number, fortgangS: number): void {
    if (via.startsWith("error:")) this.fehler += 1;
    if (via === "WACHHUND") this.wachhund += 1;
    // Ein abgebrochener Lauf endet gewollt — seine Stille bis zum Abbruch zählt trotzdem (so stand es am 15.10.).
    const fortgang = Number.isFinite(fortgangS) && fortgangS > 0 ? fortgangS : 0;
    this.luecke(echtMs - fortgang * 1000);
  }

  /** Die Zeile (nur Zahlen und der Name des Abbruchs). */
  zeile(abbruch: string | null): string {
    return (
      `[tts-zug] stuecke=${this.stuecke} datei=${this.datei} pcm=${this.pcm} neu=${this.neu} weiter=${this.weiter} ` +
      `fehler=${this.fehler} wachhund=${this.wachhund} luecke_max=${(this.lueckeMaxMs / 1000).toFixed(2)}s ` +
      `wait_max=${Math.round(this.waitMaxMs)}ms synth_max=${Math.round(this.synthMaxMs)}ms abbruch=${abbruch ?? "-"}`
    );
  }
}
